import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { BookOpen, ArrowLeft, ExternalLink, Copy, Check, ChevronDown, ChevronUp, Clock, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getAuthHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  // legacy
  'ministry_plus', 'church_team', 'pro',
];

export default function URLSummaryPage() {
  const location = useLocation();
  const state = location.state;
  const [copied, setCopied] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
        if (response.ok) setUser(await response.json());
      } catch {}
    };
    checkAuth();
  }, []);

  if (!state) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="text-center">
          <p className="text-stone-600 mb-4">No summary to display. Return to dashboard.</p>
          <Link to="/dashboard" className="text-[#1e3a8a] hover:underline font-medium">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const {
    url,
    source_type,
    title,
    overview,
    summary,
    key_insights = [],
    key_scriptures = [],
    notable_quotes = [],
    raw_transcript = '',
  } = state;

  const displayTitle = title || 'Research Summary';
  const displayOverview = overview || summary || '';

  const wordCount = raw_transcript
    ? raw_transcript.trim().split(/\s+/).filter(Boolean).length
    : 0;

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getYouTubeVideoId = (u) => {
    const match = u?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const openYouTubeAtTimestamp = (seconds) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}&t=${Math.floor(seconds)}s`, '_blank');
    }
  };

  const canExportPDF = PAID_TIERS.includes(user?.subscription_tier) || user?.is_early_adopter;

  const handleExportPDF = async () => {
    if (!canExportPDF) {
      toast.error('PDF export is available for Pastor Pro subscribers and early adopters. Please upgrade to Pastor Pro.');
      return;
    }

    setIsExporting(true);
    try {
      const response = await fetch(`${API}/summarize-url/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ url, source_type, title: displayTitle, overview: displayOverview, key_insights, key_scriptures, notable_quotes })
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${displayTitle}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
        toast.success('PDF exported successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to export PDF');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(raw_transcript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = raw_transcript;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Back link + Export */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <Button
            onClick={handleExportPDF}
            disabled={isExporting}
            variant={canExportPDF ? "outline" : "ghost"}
            size="sm"
            className={`flex items-center gap-2 ${
              canExportPDF
                ? 'text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white border-[#1e3a8a]/30'
                : 'text-stone-400 cursor-not-allowed'
            }`}
            title={canExportPDF ? 'Export as PDF' : 'PDF export is available for Pastor Pro subscribers'}
            data-testid="export-pdf-btn"
          >
            {isExporting ? (
              <>
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                <span className="hidden sm:inline">Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Export PDF</span>
              </>
            )}
          </Button>
        </div>

        {/* Title */}
        <h1 className="font-serif text-3xl font-bold text-[#1e3a8a] mb-2 leading-tight">
          {displayTitle}
        </h1>

        {/* Source URL */}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition-colors mb-6"
          >
            <ExternalLink className="w-3 h-3" />
            <span className="truncate max-w-xs">{url}</span>
          </a>
        )}

        <div className="space-y-6">

          {/* Overview */}
          {displayOverview && (
            <section>
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
                Overview
              </h2>
              <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <p className="text-stone-700 leading-relaxed">{displayOverview}</p>
              </div>
            </section>
          )}

          {/* Key Insights */}
          {key_insights.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
                Key Insights
              </h2>
              <ul className="space-y-2">
                {key_insights.map((insight, i) => {
                  // Defensive: handle both the current {text, time} shape and plain strings.
                  const text = typeof insight === 'string' ? insight : insight.text;
                  const time = typeof insight === 'string' ? null : insight.time;
                  const canJump = time != null && source_type === 'youtube' && !!url;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-3 bg-white rounded-xl border-l-4 border-[#1e3a8a] border border-stone-100 p-4 shadow-sm"
                    >
                      <span className="w-5 h-5 rounded-full bg-[#1e3a8a]/10 text-[#1e3a8a] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-stone-700 text-sm leading-relaxed">{text}</span>
                        {time != null && (
                          canJump ? (
                            <button
                              onClick={() => openYouTubeAtTimestamp(time)}
                              className="ml-2 inline-flex items-center gap-1 timestamp-pill hover:bg-[#0ea5e9] hover:text-white transition-colors align-middle"
                              title={`Jump to ${formatTimestamp(time)} on YouTube`}
                            >
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(time)}
                            </button>
                          ) : (
                            <span className="ml-2 inline-flex items-center gap-1 timestamp-pill align-middle">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(time)}
                            </span>
                          )
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Key Scriptures */}
          {key_scriptures.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
                Key Scriptures
              </h2>
              <div className="space-y-2">
                {key_scriptures.map((scripture, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 bg-teal-50 border border-teal-100 rounded-xl p-4 shadow-sm"
                  >
                    <BookOpen className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                    <p className="text-teal-800 text-sm leading-relaxed">{scripture}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Notable Quotes */}
          {notable_quotes.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">
                Notable Quotes
              </h2>
              <div className="space-y-3">
                {notable_quotes.map((quote, i) => (
                  <blockquote
                    key={i}
                    className="border-l-4 border-amber-400 bg-amber-50 rounded-r-xl pl-5 pr-4 py-4 shadow-sm"
                  >
                    <p className="text-amber-900 italic text-sm leading-relaxed">"{quote}"</p>
                  </blockquote>
                ))}
              </div>
            </section>
          )}

          {/* Full Transcript */}
          {raw_transcript && (
            <section>
              <button
                onClick={() => setTranscriptOpen(o => !o)}
                className="w-full flex items-center justify-between text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 hover:text-stone-600 transition-colors group"
              >
                <span>Full Transcript</span>
                <span className="flex items-center gap-2 normal-case font-normal text-stone-400 group-hover:text-stone-600">
                  <span className="text-[11px]">{wordCount.toLocaleString()} words</span>
                  {transcriptOpen
                    ? <ChevronUp className="w-4 h-4" />
                    : <ChevronDown className="w-4 h-4" />
                  }
                </span>
              </button>

              {transcriptOpen && (
                <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                  {/* Copy bar */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100 bg-stone-50">
                    <span className="text-xs text-stone-400">
                      {source_type === 'youtube' ? 'YouTube transcript' : 'Article text'}
                    </span>
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-[#1e3a8a] text-white hover:bg-[#1e3a8a]/90 transition-colors"
                    >
                      {copied
                        ? <><Check className="w-3.5 h-3.5" /> Copied!</>
                        : <><Copy className="w-3.5 h-3.5" /> Copy All</>
                      }
                    </button>
                  </div>

                  {/* Transcript body */}
                  <div className="p-5 max-h-[32rem] overflow-y-auto">
                    <p className="text-stone-700 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                      {raw_transcript}
                    </p>
                  </div>
                </div>
              )}
            </section>
          )}

        </div>
      </div>
    </div>
  );
}
